import { Component, ElementRef, Input, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ChartNode, ChartOrientation } from '../../models/chart-node.model';

export const MIN_ZOOM = 0.4;
export const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const MAX_COMPACT_COLUMNS = 6;

interface ChildGroups {
  leaves: ChartNode[] | null;
  branches: ChartNode[];
  columns: string | null;
}

// Generic renderer for a tree of ChartNodes. Each node's content comes from `nodeTemplate`;
// this component only draws the hierarchy, the connector lines and the pan/zoom viewport.
@Component({
  selector: 'app-hierarchy-chart',
  templateUrl: './hierarchy-chart.component.html',
  styleUrls: ['./hierarchy-chart.component.scss'],
})
export class HierarchyChartComponent implements OnInit, OnDestroy {
  @Input() roots: ChartNode[] = [];
  @Input() orientation: ChartOrientation = 'top';
  @Input() nodeTemplate: TemplateRef<any>;
  // When set, only nodes whose uid is in the set are rendered.
  @Input() visibleUids: Set<string> | null = null;
  // Top-down only: siblings without visible children of their own are wrapped into a grid
  // instead of one long row, which keeps flat hierarchies narrow.
  // While exporting the chart is drawn at 100% and zoom changes are ignored: Kendo's drawDOM
  // scales boxes but not text when the page has a CSS zoom.
  @Input() exportMode = false;
  @Input() compact = false;
  // Minimum number of grid columns; larger groups get more, up to MAX_COMPACT_COLUMNS.
  @Input() compactColumns = 3;

  @ViewChild('viewport', { static: true }) viewport: ElementRef<HTMLElement>;
  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLElement>;
  @ViewChild('tree', { static: true }) tree: ElementRef<HTMLElement>;

  zoom = 1;

  private panStart: { x: number, y: number, left: number, top: number } | null = null;
  private readonly onWheel = (event: WheelEvent) => this.handleWheel(event);

  constructor(private zone: NgZone) {
  }

  ngOnInit() {
    // Registered manually so it can be non-passive (preventDefault stops the browser's page zoom)
    this.zone.runOutsideAngular(() => {
      this.viewport.nativeElement.addEventListener('wheel', this.onWheel, { passive: false });
    });
  }

  ngOnDestroy() {
    this.viewport.nativeElement.removeEventListener('wheel', this.onWheel);
  }

  get canvasElement(): HTMLElement {
    return this.canvas.nativeElement;
  }

  // The tree itself, sized to its content (the canvas is at least as wide as the viewport).
  get treeElement(): HTMLElement {
    return this.tree.nativeElement;
  }

  // Splits siblings into those with visible children (laid out as branches) and those without,
  // which the compact layout wraps into a roughly square grid once there are enough of them.
  groupChildren(nodes: ChartNode[]): ChildGroups {
    const shown = this.visible(nodes);
    if (!this.compact || this.orientation !== 'top') {
      return { leaves: null, branches: shown, columns: null };
    }
    const isLeaf = (node: ChartNode) => !node.expanded || !this.visible(node.children).length;
    const leaves = shown.filter(isLeaf);
    if (leaves.length <= this.compactColumns) {
      return { leaves: null, branches: shown, columns: null };
    }
    const columns = Math.min(MAX_COMPACT_COLUMNS, Math.max(this.compactColumns, Math.ceil(Math.sqrt(leaves.length))));
    return { leaves, branches: shown.filter(node => !isLeaf(node)), columns: `repeat(${columns}, auto)` };
  }

  visible(nodes: ChartNode[]): ChartNode[] {
    return this.visibleUids ? nodes.filter(node => this.visibleUids.has(node.uid)) : nodes;
  }

  trackByUid(_index: number, node: ChartNode) {
    return node.uid;
  }

  // Zoom

  setZoom(value: number) {
    if (this.exportMode) {
      return;
    }
    this.zoom = Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)) * 100) / 100;
  }

  zoomIn() {
    this.setZoom(this.zoom + ZOOM_STEP);
  }

  zoomOut() {
    this.setZoom(this.zoom - ZOOM_STEP);
  }

  resetZoom() {
    this.setZoom(1);
  }

  // Scales the chart so it fits the viewport (never enlarging past 100%), then centres it.
  fit() {
    if (this.exportMode) {
      return;
    }
    const viewport = this.viewport.nativeElement;
    const canvas = this.canvas.nativeElement;
    const width = canvas.scrollWidth / this.zoom;
    const height = canvas.scrollHeight / this.zoom;
    if (!width || !height) {
      return;
    }
    this.setZoom(Math.min(1, viewport.clientWidth / width, viewport.clientHeight / height));
    setTimeout(() => {
      viewport.scrollLeft = (viewport.scrollWidth - viewport.clientWidth) / 2;
      viewport.scrollTop = 0;
    });
  }

  private handleWheel(event: WheelEvent) {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    event.preventDefault();
    this.zone.run(() => {
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    });
  }

  // Scrolling

  scrollToNode(uid: string) {
    // Wait for the expanded branch to render first
    setTimeout(() => {
      const element = this.canvas.nativeElement.querySelector(`[data-uid="${CSS.escape(uid)}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    });
  }

  // Panning by dragging the background

  onPointerDown(event: PointerEvent) {
    const target = event.target as HTMLElement;
    if (event.button !== 0 || target.closest('.hierarchy-node')) {
      return;
    }
    const viewport = this.viewport.nativeElement;
    this.panStart = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.panStart) {
      return;
    }
    const viewport = this.viewport.nativeElement;
    viewport.scrollLeft = this.panStart.left - (event.clientX - this.panStart.x);
    viewport.scrollTop = this.panStart.top - (event.clientY - this.panStart.y);
  }

  onPointerUp(event: PointerEvent) {
    if (this.panStart) {
      this.viewport.nativeElement.releasePointerCapture(event.pointerId);
      this.panStart = null;
    }
  }

  get isPanning() {
    return !!this.panStart;
  }
}
